import * as Cesium from "cesium";
import Type from "../Types";
import { createUniqueId } from "../Utility";
import { insertByPriority } from "../Utility";

export async function loadKmlTypeAsync({
  fileOrBlob,
  kmlText,
  fileName,
  id: passedId,
  viewerRef,
  setMapObjects,
  clampToGround = true,
  zoomToDataSource = true,
  isUrl = false,
  url = "",
  projectId = null,
  serverLayerId = null,
  isEdited = false,
  isDisableLoading = false,
}) {
  const viewer = viewerRef?.current;
  if (!viewer) return null;

  const isUpdateExisting = !!passedId;
  const id = passedId || createUniqueId();

  if (!isUpdateExisting) {
    const newMapObject = {
      id,
      name: "KML",
      entities: [],
      type: (fileName || "").toLowerCase().endsWith(".kmz") ? Type.ASSET.KMZ : Type.ASSET.KML,
      file_name: fileName,
      file_type: (fileName || "").toLowerCase().endsWith(".kmz") ? ".kmz" : ".kml",
      inner_entities: [],
      visible: !isDisableLoading,
      dataSource: null,
      label_visible: false,
      geojson: null,
      original_geojson: null,
      view_type: "line",
      osm_data: null,
      osmDataSource: null,
      is_edited: isEdited,
      layer_id: serverLayerId,
      url: url ? url : isUrl ? fileName : "",
      loading: true,
      priority: 0,
      _priorityOrder: typeof performance !== "undefined" ? performance.now() : Date.now(),
      project_id: projectId,
      isDisableLoading,
    };

    setMapObjects?.((prev) => insertByPriority(prev ?? [], newMapObject));
  } else {
    // update existing mapObject + remove old datasource
    setMapObjects?.((prev) => {
      const arr = [...(prev ?? [])];
      const idx = arr.findIndex((o) => o.id === id);
      if (idx === -1) return arr;

      const current = arr[idx];

      if (current.dataSource && viewer?.dataSources?.remove) {
        try {
          viewer.dataSources.remove(current.dataSource);
        } catch (_) {}
      }

      arr[idx] = {
        ...current,
        file_name: fileName ?? current.file_name,
        file_type: (fileName || current.file_name || "").toLowerCase().endsWith(".kmz") ? ".kmz" : ".kml",
        is_edited: isEdited,
        layer_id: serverLayerId ?? current.layer_id,
        url: url ? url : isUrl && fileName ? fileName : current.url,
        loading: true,
        dataSource: null,
        project_id: projectId ?? current.project_id,
        isDisableLoading: typeof isDisableLoading === "boolean" ? isDisableLoading : current.isDisableLoading,
      };

      return arr;
    });
  }

  const waitFor = async (fn, timeoutMs = 4000, intervalMs = 100) => {
    const start = Date.now();
    while (Date.now() - start < timeoutMs) {
      try {
        if (fn()) return true;
      } catch (_) {}
      await new Promise((r) => setTimeout(r, intervalMs));
    }
    return false;
  };

  const data = fileOrBlob ?? new Blob([kmlText ?? ""], { type: "application/vnd.google-earth.kml+xml" });

  let ds = null;

  try {
    ds = await Cesium.KmlDataSource.load(data, {
      camera: viewer.scene.camera,
      canvas: viewer.scene.canvas,
      clampToGround,
    });
  } catch (e) {
    // Stop loading state and avoid unhandled rejection
    setMapObjects?.((prev) =>
      (prev ?? []).map((o) =>
        o.id === id
          ? {
              ...o,
              dataSource: null,
              loading: false,
            }
          : o
      )
    );
    return null;
  }

  try {
    viewer.dataSources.add(ds);
  } catch (e) {
    // If add fails, clean up datasource object
    try {
      ds?.destroy?.();
    } catch (_) {}

    setMapObjects?.((prev) =>
      (prev ?? []).map((o) =>
        o.id === id
          ? {
              ...o,
              dataSource: null,
              loading: false,
            }
          : o
      )
    );
    return null;
  }

  // Set datasource into mapObject + stop loading
  setMapObjects?.((prev) =>
    (prev ?? []).map((o) =>
      o.id === id
        ? {
            ...o,
            dataSource: ds,
            loading: false,
          }
        : o
    )
  );

  // Optional zoom
  if (zoomToDataSource) {
    try {
      // If clamping is on, give globe/terrain a moment to become ready (reduces matrix/pick related runtime errors)
      if (clampToGround) {
        await waitFor(() => !!viewer?.scene?.globe && viewer.scene.globe.tilesLoaded === true, 4000, 100);
      }

      if (ds.entities.values.length > 0) {
        await viewer.flyTo(ds);
      }
    } catch (_) {}
  }

  return ds;
}
